import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, finalize, of, retry, shareReplay, tap, timer } from 'rxjs';
import {
  BookkeepingConfig,
  Brand,
  BrandCreateRequest,
  BrandUpdateRequest,
  DialingCode,
  Language,
  PriceList,
  Skill,
  Skillset,
  VatRegime,
} from './brand.model';
import { environment } from '../../../environments/environment';

const CONTRACT_BASE = environment.contractUrl;
const PHONESERVICE_BASE = environment.phoneServiceUrl;
const LOCALIZATION_BASE = environment.localizationV2Url;

/** Stable identifier for each lookup endpoint — keys the in-flight + loading maps. */
type LookupKey =
  | 'languages'
  | 'priceLists'
  | 'dialingCodes'
  | 'skills'
  | 'skillsets'
  | 'vatRegimes'
  | 'bookkeepingConfigs';

/**
 * Backs the `/admin/brands` routes. Mutating calls (create/update/activate/deactivate)
 * always hit the network. Lookup reads are cached in signals — the first subscriber
 * triggers the HTTP fetch; later subscribers reuse the cached value.
 *
 * Three layers of robustness on lookups:
 * 1. **Cache**: signal-backed; once loaded, subsequent calls return synchronously.
 * 2. **In-flight dedupe**: concurrent first-callers share one HTTP request via
 *    `shareReplay`, so a parallel forkJoin never double-fetches.
 * 3. **Retry**: one retry with 600ms backoff on transient failures.
 */
@Injectable({ providedIn: 'root' })
export class BrandService {
  private readonly http = inject(HttpClient);

  /**
   * Persists the "Hide inactive" toggle across navigation. Default `true` —
   * day-to-day admin work targets active brands; inactive ones are the
   * exception and stay one toggle away. The service is provided in root, so
   * this signal outlives BrandListComponent.
   */
  readonly hideInactive = signal(true);

  private readonly _languages = signal<Language[] | null>(null);
  private readonly _priceLists = signal<PriceList[] | null>(null);
  private readonly _dialingCodes = signal<DialingCode[] | null>(null);
  private readonly _skills = signal<Skill[] | null>(null);
  private readonly _skillsets = signal<Skillset[] | null>(null);
  private readonly _vatRegimes = signal<VatRegime[] | null>(null);
  private readonly _bookkeepingConfigs = signal<BookkeepingConfig[] | null>(null);

  readonly languages: Signal<readonly Language[]> = computed(() => this._languages() ?? []);
  readonly priceLists: Signal<readonly PriceList[]> = computed(() => this._priceLists() ?? []);
  readonly dialingCodes: Signal<readonly DialingCode[]> = computed(() => this._dialingCodes() ?? []);
  readonly skills: Signal<readonly Skill[]> = computed(() => this._skills() ?? []);
  readonly skillsets: Signal<readonly Skillset[]> = computed(() => this._skillsets() ?? []);
  readonly vatRegimes: Signal<readonly VatRegime[]> = computed(() => this._vatRegimes() ?? []);
  readonly bookkeepingConfigs: Signal<readonly BookkeepingConfig[]> = computed(
    () => this._bookkeepingConfigs() ?? [],
  );

  /** Per-key loading state; true while that lookup is in-flight (first load only). */
  private readonly _loading = signal<ReadonlySet<LookupKey>>(new Set());
  /** True if ANY lookup is in flight — useful for a global "loading lookups" affordance. */
  readonly anyLookupLoading: Signal<boolean> = computed(() => this._loading().size > 0);

  isLookupLoading(key: LookupKey): boolean {
    return this._loading().has(key);
  }

  /** Convenience signal for the dialingCodes case (the slowest lookup). */
  readonly dialingCodesLoading: Signal<boolean> = computed(() => this._loading().has('dialingCodes'));

  /** In-flight observables, deduped by key — see `cached()`. */
  private readonly inflight = new Map<LookupKey, Observable<unknown>>();

  // === Brands (no caching — list/detail must reflect mutations) ===

  listBrands(includeInactive = false): Observable<Brand[]> {
    const params = includeInactive ? new HttpParams().set('includeInactive', 'true') : undefined;
    return this.http.get<Brand[]>(`${CONTRACT_BASE}/brands`, { params });
  }

  getBrand(id: number): Observable<Brand> {
    return this.http.get<Brand>(`${CONTRACT_BASE}/brands/${id}`);
  }

  createBrand(body: BrandCreateRequest): Observable<Brand> {
    return this.http.post<Brand>(`${CONTRACT_BASE}/brands`, body);
  }

  updateBrand(id: number, body: BrandUpdateRequest): Observable<Brand> {
    return this.http.patch<Brand>(`${CONTRACT_BASE}/brands/${id}`, body);
  }

  activateBrand(id: number): Observable<Brand> {
    return this.updateBrand(id, { active: true });
  }

  deactivateBrand(id: number): Observable<Brand> {
    return this.updateBrand(id, { active: false });
  }

  // === Lookups (signal-cached + in-flight deduped + single retry) ===

  loadLanguages(): Observable<Language[]> {
    return this.cached('languages', this._languages, () =>
      this.http.get<Language[]>(`${PHONESERVICE_BASE}/languages`),
    );
  }

  /**
   * Create mode: no `brandId` → fetch the full catalogue.
   * Edit mode: pass `brandId` → fetch only that brand's price lists.
   *
   * Not cached — context-dependent on `brandId` so memoising would risk
   * serving a different brand's filtered list.
   */
  loadPriceLists(brandId?: number): Observable<PriceList[]> {
    const options = brandId != null
      ? { params: new HttpParams().set('brandId', String(brandId)) }
      : {};
    return this.http.get<PriceList[]>(`${CONTRACT_BASE}/pricelists`, options)
      .pipe(this.lookupRetry(), tap(value => this._priceLists.set(value)));
  }

  loadDialingCodes(): Observable<DialingCode[]> {
    return this.cached('dialingCodes', this._dialingCodes, () =>
      this.http.get<DialingCode[]>(`${LOCALIZATION_BASE}/dialingcodes/cities`),
    );
  }

  loadSkills(): Observable<Skill[]> {
    return this.cached('skills', this._skills, () =>
      this.http.get<Skill[]>(`${PHONESERVICE_BASE}/skills`),
    );
  }

  loadSkillsets(): Observable<Skillset[]> {
    return this.cached('skillsets', this._skillsets, () =>
      this.http.get<Skillset[]>(`${PHONESERVICE_BASE}/skills/skillsets`),
    );
  }

  loadVatRegimes(): Observable<VatRegime[]> {
    return this.cached('vatRegimes', this._vatRegimes, () =>
      this.http.get<VatRegime[]>(`${CONTRACT_BASE}/vatregimes`),
    );
  }

  loadBookkeepingConfigs(): Observable<BookkeepingConfig[]> {
    return this.cached('bookkeepingConfigs', this._bookkeepingConfigs, () =>
      this.http.get<BookkeepingConfig[]>(`${CONTRACT_BASE}/bookkeepingconfigurations`),
    );
  }

  /**
   * Cache + in-flight dedupe + loading-flag plumbing for a lookup endpoint.
   *  - If the cache is populated → return synchronously.
   *  - If a request is already in flight for this key → share its observable.
   *  - Otherwise fire the fetch with one retry, mark loading until it settles.
   */
  private cached<T>(
    key: LookupKey,
    store: WritableSignal<T[] | null>,
    fetch: () => Observable<T[]>,
  ): Observable<T[]> {
    const cached = store();
    if (cached !== null) return of(cached);

    const existing = this.inflight.get(key) as Observable<T[]> | undefined;
    if (existing) return existing;

    this.setLoading(key, true);
    const req = fetch().pipe(
      this.lookupRetry(),
      tap(value => store.set(value)),
      finalize(() => {
        this.inflight.delete(key);
        this.setLoading(key, false);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inflight.set(key, req);
    return req;
  }

  /** One retry with 600ms backoff — covers transient 5xx / network blips. */
  private lookupRetry<T>() {
    return retry<T>({ count: 1, delay: () => timer(600) });
  }

  private setLoading(key: LookupKey, on: boolean): void {
    this._loading.update(prev => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }
}
